import React from 'react';
import { Button } from '../atoms/button';
import { Badge } from '../atoms/badge';
import { Rocket } from 'lucide-react';

export interface UserLandsStepAction {
    text: string;
    action: () => void;
    variant?: 'primary' | 'outline';
}

export interface UserLandsStepData {
    stepNumber: number;
    videoLink: string;
    badge?: {
        text: string;
        icon?: 'rocket';
    };
    title: string;
    description: string;
    actions: {
        left?: UserLandsStepAction;
        right?: UserLandsStepAction;
    };
}

export interface UserLandsStepProps {
    data: UserLandsStepData;
    isLastStep: boolean;
    currentStep: number;
    totalSteps: number;
    isWeb: boolean;
}

export const UserLandsStep: React.FC<UserLandsStepProps> = ({
    data,
    isLastStep,
    currentStep,
    totalSteps,
    isWeb,
}) => {
    const renderBadgeIcon = () => {
        if (data.badge?.icon === 'rocket') {
            return <Rocket className="w-4 h-4" />;
        }
        return null;
    };

    // Add autoplay and loop parameters to YouTube URL
    const getVideoUrl = () => {
        const url = new URL(data.videoLink);
        url.searchParams.set('autoplay', '1');
        url.searchParams.set('loop', '1');
        url.searchParams.set('mute', '1'); // Required for autoplay in most browsers
        url.searchParams.set('controls', '0'); // Hide controls/bottom bar

        // For loop to work, we need to add the video ID as playlist
        const videoId = url.pathname.split('/').pop() || url.searchParams.get('v');
        if (videoId) {
            url.searchParams.set('playlist', videoId);
        }

        return url.toString();
    };

    return (
        <div className="flex flex-col w-full">
            {/* Video Section */}
            <div className="w-full aspect-video bg-gray-100 relative overflow-hidden left-[-1px]">
                <iframe
                    className="w-full h-full"
                    src={getVideoUrl()}
                    title="YouTube video"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                />
            </div>

            {/* Content Section */}
            <div className="p-6 flex flex-col mt-3">
                {/* Badge */}
                {data.badge && (
                    <div className="flex items-center gap-2">
                        <Badge variant="success" size="md" className="flex items-center px-2 py-1 text-xs leading-4 font-medium text-[#067647] border-[#ABEFC6] bg-[#ECFDF3]">
                            {/* {renderBadgeIcon()}
                            <span>{data.badge.text}</span> */}
                            🚀 <span>{data.badge.text}</span>
                        </Badge>
                    </div>
                )}

                {/* Title */}
                <h2 className={`font-semibold text-primary-700 mt-3 ${isWeb ? 'text-xl' : 'text-lg'}`}>
                    {data.title}
                </h2>

                {/* Description */}
                <p className={`font-normal text-gray-600 mt-4 ${isWeb ? 'text-sm' : 'text-xs'}`}>
                    {data.description}
                </p>

                {/* Actions */}
                <div className="flex items-center justify-between mt-6">
                    {/* Steps Progress - Only show if NOT last step */}
                    {!isLastStep && (
                        <div className="flex items-center gap-2">
                            {Array.from({ length: totalSteps }).map((_, index) => (
                                <div
                                    key={index}
                                    className={`h-2 rounded-full transition-all duration-300 ${index + 1 === currentStep
                                        ? 'w-8 bg-[#333A63]'
                                        : index + 1 < currentStep
                                            ? 'w-2 bg-[#17B26A]'
                                            : 'w-2 bg-[#D5D7DA]'
                                        }`}
                                />
                            ))}
                            <span className="ml-2 text-xs font-normal text-gray-800">
                                {currentStep} of {totalSteps}
                            </span>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className={`flex gap-3 ${isLastStep ? 'w-full justify-between' : 'ml-auto'}`}>
                        {/* Left Button */}
                        {data.actions.left && (
                            <Button
                                variant={data.actions.left.variant || 'outline'}
                                size={isWeb ? 'sm' : 'xs'}
                                onClick={data.actions.left.action}
                                className="px-6"
                            >
                                {data.actions.left.text}
                            </Button>
                        )}

                        {/* Right Button */}
                        {data.actions.right && (
                            <Button
                                variant={data.actions.right.variant || 'primary'}
                                size={isWeb ? 'sm' : 'xs'}
                                onClick={data.actions.right.action}
                                className="px-6"
                            >
                                {data.actions.right.text}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
